import React, { useState, useRef, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";
import { RootState } from "../../../store";
import { LanguageSelector } from "../../language-selector/LanguageSelector";
import { authService } from "../../authorization/authService";
import ThemeToggle from "../../theme-toggle";
import Footer from "../../footer";
import styles from "./PublicLayout.module.scss";

const cx = classNames.bind(styles);

const PublicLayout: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.session);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu]);

  const handleLogout = async () => {
    setShowUserMenu(false);
    await authService.signOut();
    navigate("/courses");
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");

  const prefix = user?.role === "ADMIN" ? "/admin" : user?.role === "CURATOR" ? "/curator" : "";

  return (
    <div className={cx("publicLayout")}>
      <header className={cx("topbar")}>
        <div className={cx("topbarInner")}>
          <div className={cx("leftSection")}>
            <Link to="/courses" className={cx("logo")}>
              <span className={cx("logoGreen")}>Nicolas</span>{" "}<span className={cx("logoNoWrap")}><span className={cx("logoGreen")}>Smith</span></span>
            </Link>
            <nav className={cx("navLinks")}>
              <Link
                to="/courses"
                className={cx("navLink", { active: isActive("/courses") || isActive("/course") })}
              >
                {t("topbar.courses", "Courses")}
              </Link>
              <Link
                to="/exercises"
                className={cx("navLink", { active: isActive("/exercises") })}
              >
                {t("topbar.exercises", "Exercises")}
              </Link>
            </nav>
          </div>

          <div className={cx("rightSection")}>
            <ThemeToggle />
            <LanguageSelector />

            {isAuthenticated && user ? (
              <div className={cx("userMenu")} ref={userMenuRef}>
                <button className={cx("userButton")} onClick={() => setShowUserMenu(!showUserMenu)}>
                  <div className={cx("avatar")}>
                    {user.avatar ? (
                      <img src={user.avatar} alt="" referrerPolicy="no-referrer" />
                    ) : (
                      <i className="fas fa-user"></i>
                    )}
                  </div>
                  <span className={cx("userName")}>{user.first_name || user.name}</span>
                  <i className="fas fa-chevron-down" style={{ fontSize: 10, color: "#8A94A6" }}></i>
                </button>
                {showUserMenu && (
                  <div className={cx("userDropdown")}>
                    <Link to="/dashboard" className={cx("dropdownItem")} onClick={() => setShowUserMenu(false)}>
                      <i className="fas fa-th-large"></i>
                      <span>{t("topbar.dashboard", "Dashboard")}</span>
                    </Link>
                    <Link to={`${prefix}/profile`} className={cx("dropdownItem")} onClick={() => setShowUserMenu(false)}>
                      <i className="fas fa-user"></i>
                      <span>{t("topbar.profile", "Profile")}</span>
                    </Link>
                    <Link to="/vocabulary" className={cx("dropdownItem")} onClick={() => setShowUserMenu(false)}>
                      <i className="fas fa-book"></i>
                      <span>{t("topbar.vocabulary", "Vocabulary")}</span>
                    </Link>
                    <hr className={cx("divider")} />
                    <button className={cx("dropdownItem", "logout")} onClick={handleLogout}>
                      <i className="fas fa-sign-out-alt"></i>
                      <span>{t("topbar.logout", "Logout")}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className={cx("authButtons")}>
                <Link to={`/login?returnTo=${encodeURIComponent(location.pathname)}`} className={cx("loginButton")}>
                  {t("signIn", "Log In")}
                </Link>
                <Link to={`/signup?returnTo=${encodeURIComponent(location.pathname)}`} className={cx("signupButton", "signupDesktop")}>
                  {t("signUp", "Sign Up")}
                </Link>
                <Link to={`/login?returnTo=${encodeURIComponent(location.pathname)}`} className={cx("signupButton", "signupMobile")}>
                  {t("signIn", "Log In")}
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className={cx("content")}>
        <div className={cx("contentInner")}>
          <Outlet />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PublicLayout;
